/**
 * Azure AI Search Configuration Form
 * Form component for configuring Azure AI Search in agents
 */

import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import type { AzureAISearchConfig } from '@/types/aiSearch.types';

interface AISearchConfigFormProps {
  config: AzureAISearchConfig;
  onChange: (updates: Partial<AzureAISearchConfig>) => void;
}

export function AISearchConfigForm({ config, onChange }: AISearchConfigFormProps) {
  const [showApiKey, setShowApiKey] = useState(false);

  return (
    <>
      {/* Endpoint */}
      <div>
        <label className="mb-2 block text-sm font-medium text-text-primary">
          Search Endpoint
          <span className="ml-1 text-error">*</span>
        </label>
        <input
          type="url"
          value={config.endpoint}
          onChange={(e) => onChange({ endpoint: e.target.value })}
          placeholder="https://your-search.search.windows.net"
          className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm transition-colors focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:ring-opacity-20"
          required
        />
        <p className="mt-1.5 text-xs text-text-secondary">
          Your Azure AI Search service endpoint (includes .search.windows.net)
        </p>
      </div>

      {/* API Key */}
      <div>
        <label className="mb-2 block text-sm font-medium text-text-primary">
          API Key (Admin Key)
          <span className="ml-1 text-error">*</span>
        </label>
        <div className="relative">
          <input
            type={showApiKey ? 'text' : 'password'}
            value={config.apiKey}
            onChange={(e) => onChange({ apiKey: e.target.value })}
            placeholder="••••••••••••••••••••••••••••••••"
            className="w-full rounded-lg border border-border-medium px-4 py-3 pr-12 text-sm transition-colors focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:ring-opacity-20"
            required
          />
          <button
            type="button"
            onClick={() => setShowApiKey(!showApiKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-text-tertiary hover:text-text-secondary"
            aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
          >
            {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <p className="mt-1.5 text-xs text-text-secondary">
          Admin key from Azure Portal → Search service → Keys
        </p>
      </div>

      {/* Index Name */}
      <div>
        <label className="mb-2 block text-sm font-medium text-text-primary">
          Index Name
          <span className="ml-1 text-error">*</span>
        </label>
        <input
          type="text"
          value={config.indexName}
          onChange={(e) => onChange({ indexName: e.target.value })}
          placeholder="office-docs-index"
          className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm transition-colors focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:ring-opacity-20"
          required
        />
        <p className="mt-1.5 text-xs text-text-secondary">
          Name of your pre-indexed search index
        </p>
      </div>

      {/* Semantic Configuration (Optional) */}
      <div>
        <label className="mb-2 block text-sm font-medium text-text-primary">
          Semantic Configuration (Optional)
        </label>
        <input
          type="text"
          value={config.semanticConfig || ''}
          onChange={(e) => onChange({ semanticConfig: e.target.value })}
          placeholder="default"
          className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm transition-colors focus:border-stellar-primary focus:outline-none"
        />
        <p className="mt-1.5 text-xs text-text-secondary">
          Semantic configuration name from your index settings (leave empty for none)
        </p>
      </div>

      {/* Max Results */}
      <div>
        <label className="mb-2 block text-sm font-medium text-text-primary">
          Maximum Results
        </label>
        <input
          type="number"
          min="1"
          max="50"
          value={config.top || 5}
          onChange={(e) => onChange({ top: parseInt(e.target.value) })}
          className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm focus:border-stellar-primary focus:outline-none"
        />
        <p className="mt-1.5 text-xs text-text-secondary">
          Number of search results to include in context (1-50, recommended: 5-10)
        </p>
      </div>

      {/* Semantic Ranking Toggle */}
      <div className="rounded-lg border-2 border-border-default bg-bg-secondary p-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={config.useSemanticRanking ?? true}
            onChange={(e) => onChange({ useSemanticRanking: e.target.checked })}
            className="h-4 w-4 rounded border-border-medium text-stellar-primary focus:ring-stellar-primary"
          />
          <div>
            <span className="text-sm font-medium text-text-primary">
              Enable Semantic Ranking
            </span>
            <p className="text-xs text-text-secondary mt-1">
              Use Azure OpenAI-powered semantic ranking for better
              relevance (requires Azure OpenAI integration)
            </p>
          </div>
        </label>
      </div>

      {/* Vector Search Toggle */}
      <div className="rounded-lg border-2 border-border-default bg-bg-secondary p-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={config.useVectorSearch ?? false}
            onChange={(e) => onChange({ useVectorSearch: e.target.checked })}
            className="h-4 w-4 rounded border-border-medium text-stellar-primary focus:ring-stellar-primary"
          />
          <div>
            <span className="text-sm font-medium text-text-primary">
              Enable Vector Search (Hybrid Mode)
            </span>
            <p className="text-xs text-text-secondary mt-1">
              Combine keyword search with vector embeddings for semantic
              similarity (requires vectorFieldName below)
            </p>
          </div>
        </label>
      </div>

      {/* Vector Field Name (shown only if vector search is enabled) */}
      {config.useVectorSearch && (
        <div>
          <label className="mb-2 block text-sm font-medium text-text-primary">
            Vector Field Name
            <span className="ml-1 text-error">*</span>
          </label>
          <input
            type="text"
            value={config.vectorFieldName || ''}
            onChange={(e) => onChange({ vectorFieldName: e.target.value })}
            placeholder="content_vector"
            className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm transition-colors focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:ring-opacity-20"
            required
          />
          <p className="mt-1.5 text-xs text-text-secondary">
            Name of the field in your index that contains vector embeddings
          </p>
        </div>
      )}

      {/* Integrated Vectorization Toggle (only relevant when vector search is on) */}
      {config.useVectorSearch && (
        <div className="rounded-lg border-2 border-border-default bg-bg-secondary p-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.useIntegratedVectorization ?? false}
              onChange={(e) =>
                onChange({ useIntegratedVectorization: e.target.checked })
              }
              className="h-4 w-4 rounded border-border-medium text-stellar-primary focus:ring-stellar-primary"
            />
            <div>
              <span className="text-sm font-medium text-text-primary">
                Use Integrated Vectorization
              </span>
              <p className="text-xs text-text-secondary mt-1">
                Let Azure AI Search generate the query embedding server-side.
                Skips the embedding round-trip (~430&nbsp;ms saved per query).
                Requires a vectorizer attached to the index&apos;s vector
                profile.
              </p>
            </div>
          </label>
        </div>
      )}

      {/* Skip Embedding for Short Queries Toggle */}
      {config.useVectorSearch && (
        <div className="rounded-lg border-2 border-border-default bg-bg-secondary p-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.skipEmbeddingForShortQueries ?? false}
              onChange={(e) =>
                onChange({ skipEmbeddingForShortQueries: e.target.checked })
              }
              className="h-4 w-4 rounded border-border-medium text-stellar-primary focus:ring-stellar-primary"
            />
            <div>
              <span className="text-sm font-medium text-text-primary">
                Skip Embedding for Short Queries
              </span>
              <p className="text-xs text-text-secondary mt-1">
                Run keyword-only search for queries of 2 words or fewer (e.g.
                &quot;GDPR&quot;, &quot;PCI compliance&quot;). Saves ~430&nbsp;ms
                on queries where exact-match terminology beats semantic
                similarity anyway.
              </p>
            </div>
          </label>
        </div>
      )}

      {/* Embedding Configuration Section (shown only if vector search is enabled) */}
      {config.useVectorSearch && (
        <>
          <div className="col-span-2 border-t-2 border-border-medium pt-6">
            <h3 className="text-lg font-semibold text-text-primary mb-2">
              Embedding Configuration
            </h3>
            <p className="text-sm text-text-secondary mb-6">
              Configure Azure OpenAI embeddings for generating query vectors.
              Required for vector and hybrid search modes.
            </p>
          </div>

          {/* Embedding Endpoint */}
          <div>
            <label className="mb-2 block text-sm font-medium text-text-primary">
              Embedding Endpoint
              <span className="ml-1 text-error">*</span>
            </label>
            <input
              type="url"
              value={config.embeddingEndpoint || ''}
              onChange={(e) => onChange({ embeddingEndpoint: e.target.value })}
              placeholder="https://your-openai.openai.azure.com/"
              className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm transition-colors focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:ring-opacity-20"
              required
            />
            <p className="mt-1.5 text-xs text-text-secondary">
              Azure OpenAI endpoint for embeddings (can be same as chat endpoint)
            </p>
          </div>

          {/* Embedding API Key */}
          <div>
            <label className="mb-2 block text-sm font-medium text-text-primary">
              Embedding API Key
              <span className="ml-1 text-error">*</span>
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={config.embeddingApiKey || ''}
                onChange={(e) => onChange({ embeddingApiKey: e.target.value })}
                placeholder="••••••••••••••••••••••••••••••••"
                className="w-full rounded-lg border border-border-medium px-4 py-3 pr-12 text-sm transition-colors focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:ring-opacity-20"
                required
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-text-tertiary hover:text-text-secondary"
                aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
              >
                {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-text-secondary">
              API key for Azure OpenAI embeddings (can be same as chat key)
            </p>
          </div>

          {/* Embedding Deployment */}
          <div>
            <label className="mb-2 block text-sm font-medium text-text-primary">
              Embedding Deployment
              <span className="ml-1 text-error">*</span>
            </label>
            <input
              type="text"
              value={config.embeddingDeployment || ''}
              onChange={(e) => onChange({ embeddingDeployment: e.target.value })}
              placeholder="text-embedding-ada-002"
              className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm transition-colors focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:ring-opacity-20"
              required
            />
            <p className="mt-1.5 text-xs text-text-secondary">
              Name of your embedding deployment in Azure OpenAI
            </p>
          </div>

          {/* Embedding Model */}
          <div>
            <label className="mb-2 block text-sm font-medium text-text-primary">
              Embedding Model
            </label>
            <select
              value={config.embeddingModel || 'ada-002'}
              onChange={(e) => onChange({
                embeddingModel: e.target.value as 'ada-002' | '3-small' | '3-large',
                embeddingDimensions: e.target.value === '3-large' ? 3072 : 1536
              })}
              className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm transition-colors focus:border-stellar-primary focus:outline-none"
            >
              <option value="ada-002">text-embedding-ada-002 (1536 dimensions)</option>
              <option value="3-small">text-embedding-3-small (1536 dimensions)</option>
              <option value="3-large">text-embedding-3-large (3072 dimensions)</option>
            </select>
            <p className="mt-1.5 text-xs text-text-secondary">
              Embedding model type (dimensions detected automatically)
            </p>
          </div>

          {/* Search Mode */}
          <div>
            <label className="mb-2 block text-sm font-medium text-text-primary">
              Search Mode
            </label>
            <select
              value={config.searchMode || 'hybrid'}
              onChange={(e) => onChange({ searchMode: e.target.value as 'keyword' | 'vector' | 'hybrid' })}
              className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm transition-colors focus:border-stellar-primary focus:outline-none"
            >
              <option value="keyword">Keyword Only (traditional full-text search)</option>
              <option value="vector">Vector Only (semantic similarity only)</option>
              <option value="hybrid">Hybrid (keyword + vector - recommended)</option>
            </select>
            <p className="mt-1.5 text-xs text-text-secondary">
              How to combine keyword and vector search results
            </p>
          </div>

          {/* Hybrid Search Weights (shown only if hybrid mode) */}
          {config.searchMode === 'hybrid' && (
            <>
              <div>
                <label className="mb-2 block text-sm font-medium text-text-primary">
                  Vector Weight
                </label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  value={config.vectorWeight ?? 0.5}
                  onChange={(e) => onChange({ vectorWeight: parseFloat(e.target.value) })}
                  className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm focus:border-stellar-primary focus:outline-none"
                />
                <p className="mt-1.5 text-xs text-text-secondary">
                  Weight for vector search results (0-1, default: 0.5)
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-text-primary">
                  Keyword Weight
                </label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  value={config.keywordWeight ?? 0.5}
                  onChange={(e) => onChange({ keywordWeight: parseFloat(e.target.value) })}
                  className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm focus:border-stellar-primary focus:outline-none"
                />
                <p className="mt-1.5 text-xs text-text-secondary">
                  Weight for keyword search results (0-1, default: 0.5)
                </p>
              </div>
            </>
          )}
        </>
      )}

      {/* Field Name Mappings Section */}
      <div className="col-span-2 border-t-2 border-border-medium pt-6">
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          Index Field Mappings (Optional)
        </h3>
        <p className="text-sm text-text-secondary mb-6">
          Customize field names if your index uses different naming
          conventions. Leave empty to use defaults.
        </p>
      </div>

      {/* Content Field Name */}
      <div>
        <label className="mb-2 block text-sm font-medium text-text-primary">
          Content Field Name
        </label>
        <input
          type="text"
          value={config.contentFieldName || ''}
          onChange={(e) => onChange({ contentFieldName: e.target.value })}
          placeholder="content (default)"
          className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm transition-colors focus:border-stellar-primary focus:outline-none"
        />
        <p className="mt-1.5 text-xs text-text-secondary">
          Field containing document content (default: &quot;content&quot;)
        </p>
      </div>

      {/* Title Field Name */}
      <div>
        <label className="mb-2 block text-sm font-medium text-text-primary">
          Title Field Name
        </label>
        <input
          type="text"
          value={config.titleFieldName || ''}
          onChange={(e) => onChange({ titleFieldName: e.target.value })}
          placeholder="title (default)"
          className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm transition-colors focus:border-stellar-primary focus:outline-none"
        />
        <p className="mt-1.5 text-xs text-text-secondary">
          Field containing document title (default: &quot;title&quot;)
        </p>
      </div>

      {/* URL Field Name */}
      <div>
        <label className="mb-2 block text-sm font-medium text-text-primary">
          URL Field Name
        </label>
        <input
          type="text"
          value={config.urlFieldName || ''}
          onChange={(e) => onChange({ urlFieldName: e.target.value })}
          placeholder="url (default)"
          className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm transition-colors focus:border-stellar-primary focus:outline-none"
        />
        <p className="mt-1.5 text-xs text-text-secondary">
          Field containing document URL (default: &quot;url&quot;)
        </p>
      </div>

      {/* Search Fields */}
      <div>
        <label className="mb-2 block text-sm font-medium text-text-primary">
          Search Fields
        </label>
        <input
          type="text"
          value={config.searchFields || ''}
          onChange={(e) => onChange({ searchFields: e.target.value })}
          placeholder="content,title (default)"
          className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm transition-colors focus:border-stellar-primary focus:outline-none"
        />
        <p className="mt-1.5 text-xs text-text-secondary">
          Comma-separated fields to search (default: &quot;content,title&quot;)
        </p>
      </div>

      {/* Retrieved Fields (select clause) */}
      <div>
        <label className="mb-2 block text-sm font-medium text-text-primary">
          Retrieved Fields
        </label>
        <input
          type="text"
          value={config.selectFields || ''}
          onChange={(e) => onChange({ selectFields: e.target.value })}
          placeholder="* (default — returns all fields)"
          className="w-full rounded-lg border border-border-medium px-4 py-3 text-sm transition-colors focus:border-stellar-primary focus:outline-none"
        />
        <p className="mt-1.5 text-xs text-text-secondary">
          Comma-separated fields to return per result. Narrow this list to
          shrink response payloads (default: &quot;*&quot;).
        </p>
      </div>
    </>
  );
}
